import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

type HeroVariant = 'card' | 'glass' | 'minimal' | 'panel' | 'spotlight' | 'bar';

interface HeroShowcaseProps {
  showSwitcher?: boolean;
  defaultVariant?: HeroVariant;
}

const STORAGE_KEY = 'hero-design-variant';

const HeroShowcase: React.FC<HeroShowcaseProps> = ({ 
  showSwitcher = true, 
  defaultVariant = 'card' 
}) => {
  const { user } = useAuth();
  const [variant, setVariant] = useState<HeroVariant>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem(STORAGE_KEY) as HeroVariant) || defaultVariant;
    }
    return defaultVariant;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, variant);
  }, [variant]);

  const ctaLink = user ? '/assessments' : '/auth?returnUrl=/assessments';
  const ctaText = 'Access the Marketing & Sales Self Audit tool';

  const variants: { key: HeroVariant; label: string }[] = [
    { key: 'card', label: 'Card' },
    { key: 'glass', label: 'Glass' },
    { key: 'minimal', label: 'Minimal' },
    { key: 'panel', label: 'Panel' },
    { key: 'spotlight', label: 'Spotlight' },
    { key: 'bar', label: 'Bar' },
  ];

  // Design 1: Current Card Style (baseline)
  const CardDesign = () => (
    <div className="absolute left-4 right-4 bottom-[8vh] sm:left-[8vw] sm:right-auto sm:bottom-[10vh] animate-fade-in max-w-2xl">
      <div className="bg-card/95 backdrop-blur-sm rounded-sm px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10 border border-border/30 shadow-lg">
        <h1 className="text-xl sm:text-2xl lg:text-4xl text-foreground font-semibold leading-relaxed mb-2">
          Assess Where Your Business Stands. 
          <br />
          Know Where to Move Next. 
        </h1>
        
        <div className="bg-primary w-full mt-6 p-4 rounded-sm" role="region" aria-label="Call to action">
          <Link 
            to={ctaLink}
            className="text-primary-foreground hover:no-underline hover:bg-primary-foreground/20 focus:bg-primary-foreground/20 focus:outline-none focus:ring-2 focus:ring-primary-foreground focus:ring-offset-2 focus:ring-offset-primary active:scale-95 transition-all duration-200 font-semibold text-base sm:text-lg lg:text-xl px-3 py-2 rounded inline-block"
            aria-label={ctaText}
          >
            {ctaText}
          </Link>
        </div>
      </div>
    </div>
  );

  // Design 2: Glassmorphism Floating Card
  const GlassDesign = () => (
    <div className="absolute left-4 right-4 bottom-[8vh] sm:left-[8vw] sm:right-auto sm:bottom-[10vh] animate-fade-in max-w-2xl">
      <div className="relative">
        {/* Gradient border effect */}
        <div className="absolute -inset-[1px] bg-gradient-to-r from-primary via-primary/50 to-secondary rounded-xl opacity-70 blur-sm" />
        <div className="relative bg-background/10 backdrop-blur-xl rounded-xl px-6 py-8 sm:px-8 sm:py-10 border border-white/20 shadow-2xl">
          <h1 className="text-xl sm:text-2xl lg:text-4xl text-white font-bold leading-tight mb-6 drop-shadow-lg">
            Assess Where Your Business Stands. 
            <br />
            <span className="text-primary-foreground/90">Know Where to Move Next.</span>
          </h1>
          
          <Link 
            to={ctaLink}
            className="inline-flex items-center gap-2 bg-primary/90 hover:bg-primary text-primary-foreground font-semibold text-base sm:text-lg px-6 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            aria-label={ctaText}
          >
            {ctaText}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );

  // Design 3: Minimal Text Overlay
  const MinimalDesign = () => (
    <div className="absolute left-4 right-4 bottom-[10vh] sm:left-[8vw] sm:right-auto sm:bottom-[12vh] animate-fade-in max-w-3xl">
      <h1 className="text-2xl sm:text-4xl lg:text-5xl text-white font-bold leading-tight mb-4 drop-shadow-2xl">
        Assess Where Your 
        <br />
        Business Stands.
      </h1>
      <p className="text-lg sm:text-xl text-white/80 mb-8 font-light drop-shadow-lg">
        Know Where to Move Next.
      </p>
      
      <Link 
        to={ctaLink}
        className="inline-block bg-white text-foreground font-semibold text-base sm:text-lg px-8 py-4 rounded-full shadow-xl hover:shadow-2xl hover:scale-105 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
        aria-label={ctaText}
      >
        {ctaText}
      </Link>
    </div>
  );

  // Design 4: Left-Aligned Panel
  const PanelDesign = () => (
    <div className="absolute inset-y-0 left-0 w-full sm:w-[45%] flex items-center animate-fade-in">
      <div className="w-full h-full bg-gradient-to-r from-background/95 via-background/80 to-transparent flex items-center">
        <div className="px-6 sm:px-10 lg:px-16 py-12 max-w-xl">
          <div className="w-12 h-1 bg-primary mb-6" />
          <h1 className="text-2xl sm:text-3xl lg:text-4xl text-foreground font-bold leading-tight mb-4">
            Assess Where Your Business Stands.
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Know Where to Move Next.
          </p>
          
          <Link 
            to={ctaLink}
            className="inline-flex items-center gap-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base sm:text-lg px-6 py-4 rounded-lg shadow-lg hover:shadow-xl hover:translate-x-1 transition-all duration-300"
            aria-label={ctaText}
          >
            {ctaText}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );

  // Design 5: Centered Spotlight
  const SpotlightDesign = () => (
    <div className="absolute inset-0 flex items-center justify-center animate-fade-in px-4">
      <div className="relative text-center max-w-3xl">
        {/* Radial gradient backdrop */}
        <div className="absolute inset-0 -m-20 bg-gradient-radial from-black/60 via-black/30 to-transparent rounded-full blur-2xl" />
        
        <div className="relative z-10">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl text-white font-bold leading-tight mb-3 drop-shadow-2xl">
            Assess Where Your Business Stands.
          </h1>
          <p className="text-xl sm:text-2xl text-white/90 mb-8 font-light">
            Know Where to Move Next.
          </p>
          
          <Link 
            to={ctaLink}
            className="inline-block border-2 border-white text-white hover:bg-white hover:text-foreground font-semibold text-base sm:text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            aria-label={ctaText}
          >
            {ctaText}
          </Link>
        </div>
      </div>
    </div>
  );

  // Design 6: Bottom Bar Announcement
  const BarDesign = () => (
    <>
      {/* Floating headline at top-left */}
      <div className="absolute top-[15vh] left-4 sm:left-[8vw] max-w-2xl animate-fade-in">
        <h1 className="text-2xl sm:text-4xl lg:text-5xl text-white font-bold leading-tight drop-shadow-2xl">
          Assess Where Your 
          <br />
          Business Stands.
        </h1>
        <p className="text-lg sm:text-xl text-white/80 mt-4 font-light drop-shadow-lg">
          Know Where to Move Next.
        </p>
      </div>
      
      {/* Bottom announcement bar */}
      <div className="absolute bottom-0 left-0 right-0 animate-fade-in">
        <div className="bg-primary/95 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-primary-foreground font-medium text-sm sm:text-base">
              Ready to discover your growth potential?
            </p>
            <Link 
              to={ctaLink}
              className="inline-flex items-center gap-2 bg-white text-primary hover:bg-primary-foreground hover:text-primary font-semibold text-sm sm:text-base px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              aria-label={ctaText}
            >
              Start Self Audit
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </>
  );

  const renderVariant = () => {
    switch (variant) {
      case 'glass': return <GlassDesign />;
      case 'minimal': return <MinimalDesign />;
      case 'panel': return <PanelDesign />;
      case 'spotlight': return <SpotlightDesign />;
      case 'bar': return <BarDesign />;
      default: return <CardDesign />;
    }
  };

  return (
    <>
      {renderVariant()}
      
      {/* Variant Switcher */}
      {showSwitcher && (
        <div className="absolute top-4 right-4 z-20 animate-fade-in">
          <div className="bg-background/90 backdrop-blur-sm rounded-lg shadow-lg border border-border p-1.5 flex flex-wrap gap-1 max-w-[280px] sm:max-w-none">
            {variants.map((v) => (
              <button
                key={v.key}
                onClick={() => setVariant(v.key)}
                className={cn(
                  "px-2.5 py-1.5 text-xs font-medium rounded-md transition-all duration-200",
                  variant === v.key 
                    ? "bg-primary text-primary-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default HeroShowcase;
